/* eslint-disable formatjs/no-literal-string-in-jsx */
import { useEffect, useState } from "react";

// eslint-disable-next-line no-restricted-imports
import ExitToAppIcon from "@mui/icons-material/ExitToApp";
import { Theme } from "@mui/material";
import Popover from "@mui/material/Popover";
import { makeStyles } from "@mui/styles";
import { useQuery } from "@tanstack/react-query";
import Draggable from "react-draggable";
import { FormattedMessage } from "react-intl";

import { Icon } from "@/shared/components/common/Icon/Icon";

import impersonateFarmSvg from "../../assets/img/impersonate-farm.svg?url";
import {
  deimpersonate,
  impersonate,
  isImpersonated,
} from "../../common/services/auth/Auth.service";
import { getFarmUsers } from "../../generated/api/farmManagement";
import { usePopover } from "../../shared/hooks/usePopover";
import { useToggle } from "../../shared/hooks/useToggle";
import { useIsMobile } from "../../shared/hooks/useWidth";
import AdminFarmSelector from "../shared/components/AdminFarmSelector/AdminFarmSelector";

import { farmsQuery } from "./api/impersonation.api";
import { ImpersonationButton } from "./ImpersonationButton";

type Props = {
  farm: { id: string; name: string };
  toggle: () => void;
};

const extractFarmUrlInfo = (
  input: string,
): { farmId: string; path: string } | null => {
  try {
    const url = new URL(input);
    const m = url.pathname.match(/^\/farm\/(\d+)(\/.*)?$/);
    if (m) return { farmId: m[1], path: url.pathname + url.search };
  } catch {
    const m = input.match(/^\/farm\/(\d+)(\/.*)?$/);
    if (m) return { farmId: m[1], path: input };
  }
  return null;
};

const ImpersonationPanel = (props: Props) => {
  const [search, setSearch] = useState("");
  const [redirectPath, setRedirectPath] = useState<string | undefined>();
  const impersonated = isImpersonated();
  const [currentFarm, setCurrentFarm] = useState<
    undefined | typeof props.farm
  >();
  const dragging = useToggle();
  const classes = useStyles();
  const isMobile = useIsMobile();
  const popover = usePopover();
  const allFarms = useQuery({
    ...farmsQuery({ search }),
    enabled: !!search.length,
  });
  const farmUsers = useQuery({
    queryKey: ["farmUsers", currentFarm?.id],
    queryFn: ({ signal }) =>
      currentFarm?.id
        ? getFarmUsers(
            { farmIds: currentFarm.id },
            { signal, skipFarmId: true },
          )
        : Promise.reject(new Error("No farm selected")),
    enabled: !!currentFarm,
  });

  useEffect(() => {
    if (impersonated) {
      setCurrentFarm(props.farm);
    }
  }, [impersonated, props.farm]);

  useEffect(() => {
    if (impersonated) {
      return;
    }

    if (!currentFarm) {
      return;
    }

    const user = farmUsers.data?.data[0];
    if (!user) {
      return;
    }
    popover.handlePopoverClose();
    impersonate(currentFarm, user.userId, redirectPath);
  }, [impersonated, currentFarm, farmUsers.data?.data, popover, redirectPath]);

  const handleStartSelectingFarms = (
    e: React.MouseEvent<HTMLButtonElement>,
  ) => {
    if (dragging.on) {
      dragging.setOff();
      return;
    }

    if (impersonated) {
      return;
    }

    popover.handlePopoverOpen(e);
  };

  const handleClearSelectedFarm = () => {
    setCurrentFarm(undefined);
    setRedirectPath(undefined);
    popover.handlePopoverClose();
    deimpersonate();
  };

  const handleOnSuggestionClear = () => {
    popover.handlePopoverClose();
  };

  const handleOnSuggestionSelect = (farm: Props["farm"]) => {
    setCurrentFarm(farm);
    popover.handlePopoverClose();
  };

  let Buttons = null;

  if (!impersonated) {
    Buttons = (
      <>
        <ImpersonationButton
          callback={handleStartSelectingFarms}
          testId="select-farm"
        >
          <img
            alt="Impersonate farm"
            className={classes.image}
            src={impersonateFarmSvg}
          />
          <FormattedMessage id="Impersonation.impersonate-farm" />
          <Icon name="caret" />
        </ImpersonationButton>
        <ImpersonationButton callback={props.toggle} testId="close">
          <ExitToAppIcon />
        </ImpersonationButton>
      </>
    );
  } else {
    Buttons = (
      <>
        <span data-testid="selected-farm-name">{currentFarm?.name}</span>
        <ImpersonationButton callback={handleClearSelectedFarm} testId="cancel">
          <Icon name="close" />
        </ImpersonationButton>
      </>
    );
  }

  const jsx = (
    <div className={classes.panel}>
      {Buttons}

      <Popover
        anchorEl={popover.anchorEl}
        classes={{ paper: classes.popoverPaper }}
        className={classes.modalRoot}
        onClose={handleOnSuggestionClear}
        open={popover.isOpen}
        PaperProps={{
          "data-testid": "farm-list",
        }}
        transformOrigin={{
          horizontal: "left",
          vertical: "bottom",
        }}
      >
        <AdminFarmSelector
          isFetching={allFarms.status === "pending" && allFarms.isEnabled}
          onSuggestionClear={handleOnSuggestionClear}
          onSuggestionSelect={handleOnSuggestionSelect}
          suggestions={allFarms.data?.data ?? []}
          getFarmsByName={(input: string) => {
            const urlInfo = extractFarmUrlInfo(input);
            if (urlInfo) {
              setCurrentFarm({ id: urlInfo.farmId, name: urlInfo.farmId });
              setRedirectPath(urlInfo.path);
              popover.handlePopoverClose();
              return;
            }
            setSearch(input);
          }}
        />
      </Popover>
    </div>
  );

  if (isMobile) {
    return jsx;
  }

  return (
    <Draggable bounds="html" disabled={popover.isOpen} onDrag={dragging.setOn}>
      {jsx}
    </Draggable>
  );
};

const useStyles = makeStyles((theme: Theme) => ({
  panel: {
    alignItems: "center",
    background: theme.palette.common.white,
    color: theme.palette.cf.cfLightBlack,
    display: "flex",
    paddingLeft: theme.spacing(1),
    position: "fixed",
    bottom: 85,
    right: 25,
    zIndex: 10000,
    boxShadow:
      "0px 1px 5px 0px rgba(0, 0, 0, 0.2), 0px 2px 2px 0px rgba(0, 0, 0, 0.14), 0px 3px 1px -2px rgba(0, 0, 0, 0.12)",
    borderRadius: 2,
    textTransform: "uppercase",
  },
  image: {
    marginRight: theme.spacing(1),
    position: "relative",
    top: "-2px",
  },
  popoverPaper: {
    width: 350,
    height: 350,
    overflow: "hidden",
  },
  modalRoot: {
    zIndex: 5147483002,
  },
}));

export { ImpersonationPanel };
